// src/pages/admin/articles/ArticleEditorDialog.jsx
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
  Box,
  Divider,
  Paper,
  Typography,
  Chip,
  Alert,
  Tabs,
  Tab,
  Card,
  CardMedia,
  CardContent,
  ButtonGroup,
} from "@mui/material";
import UploadFileDialog from "../../../../../Components/tools/uploadfile";
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  Title as TitleIcon,
  Link as LinkIcon,
  FormatListBulleted as BulletedIcon,
  FormatListNumbered as NumberedIcon,
  LooksOne as H1Icon,
  LooksTwo as H2Icon,
  Looks3 as H3Icon,
  Preview as PreviewIcon,
  Code as CodeIcon,
  FormatQuote as QuoteIcon,
  HorizontalRule as HrIcon,
  Image as ImageIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Help as HelpIcon,
} from "@mui/icons-material";

/**
 * Enhanced Markdown -> HTML converter with support for:
 * headings, bold, italics, links, lists, code blocks, blockquotes, horizontal rules
 */
function mdToHtml(src) {
  if (!src) return "";

  let html = src;

  // Escape angle brackets (basic safety)
  html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Code blocks ```code``` (must come before inline code)
  html = html.replace(/```([^`]+)```/g, "<pre><code>$1</code></pre>");
  
  // Inline code `code`
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");

  // Blockquotes > text
  html = html.replace(/^> (.*)$/gm, "<blockquote>$1</blockquote>");

  // Horizontal rule ---
  html = html.replace(/^---$/gm, "<hr />");

  // Headings: ###, ##, #
  html = html.replace(/^### (.*)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.*)$/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.*)$/gm, "<h1>$1</h1>");

  // Bold **text**
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // Italic *text*
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");

  // Links [text](url)
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Images ![alt](url)
  html = html.replace(
    /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g,
    '<img src="$2" alt="$1" style="max-width: 100%; height: auto;" />'
  );

  // Numbered lists
  html = html.replace(/(^|\n)(\d+\.\s.+(\n(?!\n).+)*)/g, (m) => {
    const items = m
      .trim()
      .split(/\n/)
      .map((line) => line.replace(/^\d+\.\s/, "").trim())
      .filter(Boolean)
      .map((li) => `<li>${li}</li>`)
      .join("");
    return `\n<ol>${items}</ol>`;
  });

  // Bulleted lists
  html = html.replace(/(^|\n)(-\s.+(\n(?!\n).+)*)/g, (m) => {
    const items = m
      .trim()
      .split(/\n/)
      .map((line) => line.replace(/^-+\s/, "").replace(/^-?\s/, "").trim())
      .filter(Boolean)
      .map((li) => `<li>${li}</li>`)
      .join("");
    return `\n<ul>${items}</ul>`;
  });

  // Paragraphs (lines not already wrapped)
  html = html
    .split(/\n{2,}/)
    .map((block) => {
      const isWrapped = /^<(h1|h2|h3|ul|ol|li|p|blockquote|pre|hr)/.test(
        block.trim()
      );
      if (isWrapped) return block;
      const lines = block
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      if (!lines.length) return "";
      return `<p>${lines.join("<br/>")}</p>`;
    })
    .join("\n");

  return html;
}

// Markdown Help Dialog
function MarkdownHelpDialog({ open, onClose }) {
  const examples = [
    { syntax: "# Heading 1", desc: "Main heading" },
    { syntax: "## Heading 2", desc: "Sub heading" },
    { syntax: "### Heading 3", desc: "Section heading" },
    { syntax: "**bold text**", desc: "Bold text" },
    { syntax: "*italic text*", desc: "Italic text" },
    { syntax: "[link text](url)", desc: "Hyperlink" },
    { syntax: "![alt text](image-url)", desc: "Image" },
    { syntax: "`code`", desc: "Inline code" },
    { syntax: "```code block```", desc: "Code block" },
    { syntax: "> quote", desc: "Blockquote" },
    { syntax: "- item", desc: "Bulleted list" },
    { syntax: "1. item", desc: "Numbered list" },
    { syntax: "---", desc: "Horizontal rule" },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Markdown Syntax Guide</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          {examples.map((ex, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={0.5}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    bgcolor: "grey.100",
                    p: 0.5,
                    borderRadius: 0.5,
                  }}
                >
                  {ex.syntax}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {ex.desc}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Toolbar button component
const ToolbarButton = ({ label, icon, onClick, tooltip, active }) => (
  <Tooltip title={tooltip || label}>
    <IconButton
      onClick={onClick}
      size="small"
      sx={{
        bgcolor: active ? "primary.lighter" : "transparent",
        "&:hover": { bgcolor: active ? "primary.light" : "action.hover" },
      }}
    >
      {icon}
    </IconButton>
  </Tooltip>
);

// IMPROVED: Helper functions for text manipulation that work with React state
function applyWrapToSelection(textareaRef, before, after = "", updateState) {
  const el = textareaRef.current;
  if (!el) return;

  // Get the actual textarea element (MUI wraps it)
  const textarea = el.querySelector('textarea') || el;
  
  const start = textarea.selectionStart ?? 0;
  const end = textarea.selectionEnd ?? 0;
  const val = textarea.value || "";
  const selected = val.slice(start, end);
  
  // Build the new value
  const newValue = val.slice(0, start) + before + selected + after + val.slice(end);
  
  // Calculate new cursor position
  const newCursorPos = start + before.length + selected.length + after.length;
  
  // Update through React state
  updateState(newValue);
  
  // Set cursor position after React updates the DOM
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(newCursorPos, newCursorPos);
  });
}

function insertAtLineStart(textareaRef, prefix, updateState) {
  const el = textareaRef.current;
  if (!el) return;

  // Get the actual textarea element (MUI wraps it)
  const textarea = el.querySelector('textarea') || el;
  
  const start = textarea.selectionStart ?? 0;
  const val = textarea.value || "";
  
  // Find the start of the current line
  const lineStart = val.lastIndexOf("\n", start - 1) + 1;
  
  // Build the new value
  const newValue = val.slice(0, lineStart) + prefix + val.slice(lineStart);
  
  // Calculate new cursor position
  const newCursorPos = start + prefix.length;
  
  // Update through React state
  updateState(newValue);
  
  // Set cursor position after React updates the DOM
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(newCursorPos, newCursorPos);
  });
}

const defaultForm = {
  title: "",
  slug: "",
  subtitle: "",
  excerpt: "",
  content_plain: "",
  category: "",
  tags: [],
  featured_image: "",
  featured_image_alt: "",
  meta_title: "",
  meta_description: "",
  is_featured: false,
  allow_comments: true,
  show_table_of_contents: false,
};

const generateSlug = (title) =>
  (title || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export default function ArticleEditorDialog({
  open,
  onClose,
  onSave,
  categories = [],
  initialData = {},
  isSaving = false,
}) {
  const [form, setForm] = React.useState({ ...defaultForm, ...initialData });
  const [showPreview, setShowPreview] = React.useState(true);
  const [imageDialogOpen, setImageDialogOpen] = React.useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0);

  const contentRef = React.useRef(null);

  React.useEffect(() => {
    setForm({
      ...defaultForm,
      ...initialData,
      content_plain: initialData.content_plain || initialData.content || "",
    });
    setActiveTab(0);
  }, [initialData, open]);

  const handleTitle = (v) => {
    setForm((prev) => ({
      ...prev,
      title: v,
      slug: prev.slug || generateSlug(v),
    }));
  };

  const handleTagsChange = (v) => {
    const arr = v
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    setForm((prev) => ({ ...prev, tags: arr }));
  };

  const handleUploadSuccess = (uploadedFile) => {
    // UploadFileDialog returns an object with url/file_url from Firebase Storage
    // Extract the URL and store it in featured_image
    const imageUrl = uploadedFile.url || uploadedFile.file_url;
    setForm((prev) => ({ ...prev, featured_image: imageUrl }));
    setImageDialogOpen(false);
  };

  // Helper to update content through state
  const updateContent = (newValue) => {
    setForm((prev) => ({ ...prev, content_plain: newValue }));
  };

  const htmlPreview = React.useMemo(
    () => mdToHtml(form.content_plain),
    [form.content_plain]
  );

  const handleSave = () => {
    const payload = {
      ...form,
      content: mdToHtml(form.content_plain),
    };
    delete payload.content_plain;
    onSave(payload);
  };

  const disabled = !form.title || !form.slug;
  const wordCount = form.content_plain.split(/\s+/).filter(Boolean).length;

  // Enhanced toolbar with all formatting options - NOW WITH WORKING FUNCTIONS
  const Toolbar = (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "grey.50",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 1,
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
          gap={0.5}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: 1, fontWeight: 600 }}
          >
            FORMATTING
          </Typography>
          <ToolbarButton
            label="H1"
            icon={<H1Icon fontSize="small" />}
            onClick={() => insertAtLineStart(contentRef, "# ", updateContent)}
            tooltip="Heading 1 (# )"
          />
          <ToolbarButton
            label="H2"
            icon={<H2Icon fontSize="small" />}
            onClick={() => insertAtLineStart(contentRef, "## ", updateContent)}
            tooltip="Heading 2 (## )"
          />
          <ToolbarButton
            label="H3"
            icon={<H3Icon fontSize="small" />}
            onClick={() => insertAtLineStart(contentRef, "### ", updateContent)}
            tooltip="Heading 3 (### )"
          />
          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
          <ToolbarButton
            label="Bold"
            icon={<BoldIcon fontSize="small" />}
            onClick={() => applyWrapToSelection(contentRef, "**", "**", updateContent)}
            tooltip="Bold (**text**)"
          />
          <ToolbarButton
            label="Italic"
            icon={<ItalicIcon fontSize="small" />}
            onClick={() => applyWrapToSelection(contentRef, "*", "*", updateContent)}
            tooltip="Italic (*text*)"
          />
          <ToolbarButton
            label="Code"
            icon={<CodeIcon fontSize="small" />}
            onClick={() => applyWrapToSelection(contentRef, "`", "`", updateContent)}
            tooltip="Inline code (`code`)"
          />
          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
          <ToolbarButton
            label="Link"
            icon={<LinkIcon fontSize="small" />}
            onClick={() => applyWrapToSelection(contentRef, "[", "](url)", updateContent)}
            tooltip="Link ([text](url))"
          />
          <ToolbarButton
            label="Image"
            icon={<ImageIcon fontSize="small" />}
            onClick={() => applyWrapToSelection(contentRef, "![alt](", ")", updateContent)}
            tooltip="Image (![alt](url))"
          />
          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
          <ToolbarButton
            label="Bulleted"
            icon={<BulletedIcon fontSize="small" />}
            onClick={() => insertAtLineStart(contentRef, "- ", updateContent)}
            tooltip="Bulleted list (- item)"
          />
          <ToolbarButton
            label="Numbered"
            icon={<NumberedIcon fontSize="small" />}
            onClick={() => insertAtLineStart(contentRef, "1. ", updateContent)}
            tooltip="Numbered list (1. item)"
          />
          <ToolbarButton
            label="Quote"
            icon={<QuoteIcon fontSize="small" />}
            onClick={() => insertAtLineStart(contentRef, "> ", updateContent)}
            tooltip="Blockquote (> text)"
          />
          <ToolbarButton
            label="HR"
            icon={<HrIcon fontSize="small" />}
            onClick={() => insertAtLineStart(contentRef, "---\n", updateContent)}
            tooltip="Horizontal rule (---)"
          />
          <Box sx={{ flexGrow: 1 }} />
          <ToolbarButton
            label="Help"
            icon={<HelpIcon fontSize="small" />}
            onClick={() => setHelpDialogOpen(true)}
            tooltip="Markdown syntax guide"
          />
          <ToolbarButton
            label="Preview"
            icon={
              showPreview ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )
            }
            onClick={() => setShowPreview((s) => !s)}
            tooltip={showPreview ? "Hide preview" : "Show preview"}
            active={showPreview}
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={`${wordCount} words`}
            sx={{ fontSize: 11 }}
          />
          <Chip
            size="small"
            label={`${form.content_plain.length} characters`}
            sx={{ fontSize: 11 }}
          />
        </Stack>
      </Stack>
    </Paper>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5" fontWeight={600}>
              {initialData?.id ? "Edit Article" : "Create New Article"}
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              px: 3,
            }}
          >
            <Tab label="Content" />
            <Tab label="Settings" />
            <Tab label="SEO & Advanced" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* TAB 0: CONTENT */}
            {activeTab === 0 && (
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Title *"
                  value={form.title}
                  onChange={(e) => handleTitle(e.target.value)}
                  variant="outlined"
                  placeholder="Enter an engaging title..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: 20,
                      fontWeight: 500,
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Subtitle"
                  value={form.subtitle}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, subtitle: e.target.value }))
                  }
                  placeholder="Optional subtitle or tagline"
                />

                {/* Editor Section */}
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 2 }}
                  >
                    <TitleIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Article Content
                    </Typography>
                  </Stack>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    Write using simple markdown syntax. The editor will
                    automatically convert your text to HTML when you save.
                  </Alert>

                  {Toolbar}

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={showPreview ? 6 : 12}>
                      <TextField
                        inputRef={contentRef}
                        fullWidth
                        multiline
                        minRows={18}
                        maxRows={30}
                        placeholder="Start writing your article here...

# Main Heading
Use markdown syntax to format your text

## Subheading
- Bullet point
- Another point

**Bold text** and *italic text* are easy to add."
                        value={form.content_plain}
                        onChange={(e) => updateContent(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontFamily: "monospace",
                            fontSize: 14,
                            lineHeight: 1.6,
                          },
                        }}
                      />
                    </Grid>

                    {showPreview && (
                      <Grid item xs={12} md={6}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 3,
                            minHeight: 400,
                            maxHeight: 600,
                            overflow: "auto",
                            bgcolor: "grey.50",
                          }}
                        >
                          <Stack spacing={1} sx={{ mb: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                            >
                              LIVE PREVIEW
                            </Typography>
                          </Stack>
                          <Box
                            dangerouslySetInnerHTML={{ __html: htmlPreview }}
                            sx={{
                              "& h1": { fontSize: "2rem", fontWeight: 700, mb: 2 },
                              "& h2": { fontSize: "1.5rem", fontWeight: 600, mb: 1.5, mt: 2 },
                              "& h3": { fontSize: "1.25rem", fontWeight: 600, mb: 1, mt: 1.5 },
                              "& p": { mb: 1.5, lineHeight: 1.7 },
                              "& ul, & ol": { pl: 3, mb: 2 },
                              "& li": { mb: 0.5 },
                              "& code": {
                                bgcolor: "grey.200",
                                px: 0.5,
                                py: 0.25,
                                borderRadius: 0.5,
                                fontSize: "0.9em",
                                fontFamily: "monospace",
                              },
                              "& pre": {
                                bgcolor: "grey.800",
                                color: "white",
                                p: 2,
                                borderRadius: 1,
                                overflow: "auto",
                                mb: 2,
                              },
                              "& blockquote": {
                                borderLeft: 4,
                                borderColor: "primary.main",
                                pl: 2,
                                py: 1,
                                bgcolor: "grey.100",
                                fontStyle: "italic",
                                mb: 2,
                              },
                              "& hr": {
                                border: 0,
                                borderTop: 2,
                                borderColor: "divider",
                                my: 3,
                              },
                              "& img": {
                                maxWidth: "100%",
                                height: "auto",
                                borderRadius: 1,
                                my: 2,
                              },
                              "& a": {
                                color: "primary.main",
                                textDecoration: "underline",
                              },
                            }}
                          />
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Excerpt"
                  value={form.excerpt}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, excerpt: e.target.value }))
                  }
                  placeholder="Brief summary of the article (used in previews)"
                  helperText={`${form.excerpt.length} characters`}
                />
              </Stack>
            )}

            {/* TAB 1: SETTINGS */}
            {activeTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Article Settings
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Slug *"
                    value={form.slug}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, slug: e.target.value }))
                    }
                    helperText="URL-friendly version of the title"
                    placeholder="article-slug-here"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={form.category}
                      label="Category"
                      onChange={(e) =>
                        setForm((p) => ({ ...p, category: e.target.value }))
                      }
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tags"
                    value={form.tags.join(", ")}
                    onChange={(e) => handleTagsChange(e.target.value)}
                    placeholder="e.g., technology, tutorial, web development"
                    helperText="Separate tags with commas"
                  />
                  <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {form.tags.map((tag, i) => (
                      <Chip
                        key={i}
                        label={tag}
                        size="small"
                        onDelete={() => {
                          const newTags = form.tags.filter((_, idx) => idx !== i);
                          setForm((p) => ({ ...p, tags: newTags }));
                        }}
                      />
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Featured Image
                  </Typography>
                  <Stack spacing={2}>
                    {form.featured_image ? (
                      <Card sx={{ maxWidth: 400 }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={form.featured_image}
                          alt="Featured"
                          sx={{ objectFit: "cover" }}
                        />
                        <CardContent>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setImageDialogOpen(true)}
                            >
                              Change
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() =>
                                setForm((p) => ({
                                  ...p,
                                  featured_image: "",
                                  featured_image_alt: "",
                                }))
                              }
                            >
                              Remove
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<ImageIcon />}
                        onClick={() => setImageDialogOpen(true)}
                        sx={{ maxWidth: 300 }}
                      >
                        Add Featured Image
                      </Button>
                    )}
                  </Stack>
                  {form.featured_image && (
                    <TextField
                      fullWidth
                      label="Image Alt Text"
                      value={form.featured_image_alt}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          featured_image_alt: e.target.value,
                        }))
                      }
                      helperText="Describe the image for accessibility"
                      sx={{ mt: 2 }}
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Display Options
                  </Typography>
                  <Stack spacing={1.5}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.is_featured}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              is_featured: e.target.checked,
                            }))
                          }
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Featured Article
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Highlight this article on the homepage
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.allow_comments}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              allow_comments: e.target.checked,
                            }))
                          }
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Allow Comments
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Enable reader comments on this article
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.show_table_of_contents}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              show_table_of_contents: e.target.checked,
                            }))
                          }
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Show Table of Contents
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Auto-generate navigation from headings
                          </Typography>
                        </Box>
                      }
                    />
                  </Stack>
                </Grid>
              </Grid>
            )}

            {/* TAB 2: SEO & ADVANCED */}
            {activeTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    SEO & Metadata
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Meta Title"
                    value={form.meta_title}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, meta_title: e.target.value }))
                    }
                    helperText={`${form.meta_title.length}/60 characters (optimal for search engines)`}
                    placeholder="Compelling title for search results"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Meta Description"
                    value={form.meta_description}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        meta_description: e.target.value,
                      }))
                    }
                    helperText={`${form.meta_description.length}/160 characters (optimal for search engines)`}
                    placeholder="Brief description that appears in search results"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      SEO Tips
                    </Typography>
                    <Typography variant="caption" component="div">
                      • Keep meta titles under 60 characters
                      <br />
                      • Keep meta descriptions under 160 characters
                      <br />
                      • Include target keywords naturally
                      <br />• Make descriptions compelling and action-oriented
                    </Typography>
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      Search Preview
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ fontWeight: 500 }}
                      >
                        {form.meta_title || form.title || "Article Title"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="success.main"
                        display="block"
                      >
                        yoursite.com/articles/{form.slug || "article-slug"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {form.meta_description ||
                          form.excerpt ||
                          "Article description will appear here..."}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "grey.50",
            px: 3,
            py: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            width="100%"
          >
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {disabled
                ? "Title and slug are required"
                : "Ready to publish"}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button onClick={onClose} size="large">
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={disabled || isSaving}
                onClick={handleSave}
                size="large"
              >
                {isSaving ? "Saving..." : initialData?.id ? "Update Article" : "Publish Article"}
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>

      <UploadFileDialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        onUploadSuccess={handleUploadSuccess}
      />

      <MarkdownHelpDialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />
    </>
  );
}